import { ListBulletsIcon, Icon as PhosphorIcon, SquaresFourIcon } from "@phosphor-icons/react";

import GalaxyAvatar from "@gx/dls/avatar/GalaxyAvatar";
import { BeaconVariant } from "@gx/dls/beacons/Beacon";
import AttioLogomark from "@gx/dls/icons/sources/AttioLogomark";
import BrowserbaseLogomark from "@gx/dls/icons/sources/BrowserbaseLogomark";
import ClickhouseLogomark from "@gx/dls/icons/sources/ClickhouseLogomark";
import ConfluenceLogomark from "@gx/dls/icons/sources/ConfluenceLogomark";
import GalaxyFileUploadIcon from "@gx/dls/icons/sources/GalaxyFileUploadIcon";
import GalaxyTextIcon from "@gx/dls/icons/sources/GalaxyTextIcon";
import GalaxyWebhookIcon from "@gx/dls/icons/sources/GalaxyWebhookIcon";
import GithubLogomark from "@gx/dls/icons/sources/GithubLogomark";
import GoogleBigqueryLogomark from "@gx/dls/icons/sources/GoogleBigqueryLogomark";
import GoogleDriveLogomark from "@gx/dls/icons/sources/GoogleDriveLogomark";
import HubspotLogomark from "@gx/dls/icons/sources/HubspotLogomark";
import JiraLogomark from "@gx/dls/icons/sources/JiraLogomark";
import LinearLogomark from "@gx/dls/icons/sources/LinearLogomark";
import MySQLLogomark from "@gx/dls/icons/sources/MySQLLogomark";
import NetsuiteLogomark from "@gx/dls/icons/sources/NetsuiteLogomark";
import NotionLogomark from "@gx/dls/icons/sources/NotionLogomark";
import PostgresLogomark from "@gx/dls/icons/sources/PostgresLogomark";
import S3Logomark from "@gx/dls/icons/sources/S3Logomark";
import SalesforceLogomark from "@gx/dls/icons/sources/SalesforceLogomark";
import SlackLogomark from "@gx/dls/icons/sources/SlackLogoIconmark";
import SnowflakeLogomark from "@gx/dls/icons/sources/SnowflakeLogomark";
import WikipediaLogomark from "@gx/dls/icons/sources/WikipediaLogomark";
import { TextVariant } from "@gx/dls/text/Text";
import {
  SourceStatus,
  SourceSyncStatus,
  SourceType,
} from "@gx/ts-contracts/services/database/models/public_pb";

import { FeatureFlag } from "@/hooks/useFeatureFlag";

export enum SourcesView {
  LIST = "LIST",
  GRID = "GRID",
}

export const DEFAULT_SOURCES_VIEW = SourcesView.LIST;

// This is intentionally the inverse of the current view
// because the icon is used to switch the view
export const SOURCE_VIEW_TO_ICON_MAP: Record<SourcesView, PhosphorIcon> = {
  [SourcesView.LIST]: SquaresFourIcon,
  [SourcesView.GRID]: ListBulletsIcon,
};

export const SOURCE_TYPE_TO_ICON_MAP: Record<SourceType, React.ReactNode> = {
  [SourceType.UNSPECIFIED]: <GalaxyAvatar size={16} />,
  [SourceType.FILE_UPLOAD]: <GalaxyFileUploadIcon height={16} />,
  [SourceType.WEBHOOK]: <GalaxyWebhookIcon height={16} />,
  [SourceType.TEXT]: <GalaxyTextIcon height={16} />,
  [SourceType.SLACK]: <SlackLogomark height={16} />,
  [SourceType.JIRA]: <JiraLogomark height={16} />,
  [SourceType.LINEAR]: <LinearLogomark height={16} />,
  [SourceType.NETSUITE]: <NetsuiteLogomark height={16} />,
  [SourceType.BROWSERBASE]: <BrowserbaseLogomark height={16} />,
  [SourceType.CLICKHOUSE]: <ClickhouseLogomark height={16} />,
  [SourceType.MYSQL]: <MySQLLogomark height={16} />,
  [SourceType.SNOWFLAKE]: <SnowflakeLogomark height={16} />,
  [SourceType.GOOGLE_DRIVE]: <GoogleDriveLogomark height={16} />,
  [SourceType.S3]: <S3Logomark height={16} />,
  [SourceType.SALESFORCE]: <SalesforceLogomark height={16} />,
  [SourceType.ATTIO]: <AttioLogomark height={16} />,
  [SourceType.NOTION]: <NotionLogomark height={16} />,
  [SourceType.POSTGRES]: <PostgresLogomark height={16} />,
  [SourceType.BIGQUERY]: <GoogleBigqueryLogomark height={16} />,
  [SourceType.HUBSPOT]: <HubspotLogomark height={16} />,
  [SourceType.GITHUB]: <GithubLogomark height={16} />,
  [SourceType.CONFLUENCE]: <ConfluenceLogomark height={16} />,
  [SourceType.WIKIPEDIA]: <WikipediaLogomark height={16} />,
};

export const SOURCE_TYPE_TO_LABEL_MAP: Record<SourceType, string> = {
  [SourceType.UNSPECIFIED]: "Need a different source?",
  [SourceType.FILE_UPLOAD]: "File Upload",
  [SourceType.WEBHOOK]: "Webhook",
  [SourceType.TEXT]: "Text",
  [SourceType.SLACK]: "Slack",
  [SourceType.JIRA]: "Jira",
  [SourceType.LINEAR]: "Linear",
  [SourceType.NETSUITE]: "NetSuite",
  [SourceType.BROWSERBASE]: "Browserbase",
  [SourceType.CLICKHOUSE]: "ClickHouse",
  [SourceType.MYSQL]: "MySQL",
  [SourceType.SNOWFLAKE]: "Snowflake",
  [SourceType.GOOGLE_DRIVE]: "Google Drive",
  [SourceType.S3]: "Amazon S3",
  [SourceType.SALESFORCE]: "Salesforce",
  [SourceType.ATTIO]: "Attio",
  [SourceType.NOTION]: "Notion",
  [SourceType.POSTGRES]: "PostgreSQL",
  [SourceType.BIGQUERY]: "BigQuery",
  [SourceType.HUBSPOT]: "HubSpot",
  [SourceType.GITHUB]: "GitHub",
  [SourceType.CONFLUENCE]: "Confluence",
  [SourceType.WIKIPEDIA]: "Wikipedia",
};

export const SOURCE_TYPE_TO_DESCRIPTION_MAP: Record<SourceType, string> = {
  [SourceType.UNSPECIFIED]: "Let us know what you need and we'll help you get connected.",
  [SourceType.FILE_UPLOAD]:
    "Direct file ingestion for documents, spreadsheets, and other structured or unstructured content.",
  [SourceType.WEBHOOK]:
    "HTTP webhook endpoint for receiving real-time event payloads from external services.",
  [SourceType.TEXT]: "Raw text input for pasting or typing content directly into Galaxy.",
  [SourceType.SLACK]:
    "Messaging and collaboration platform designed for teams to communicate and work together efficiently.",
  [SourceType.JIRA]:
    "Project tracking and issue management tool for agile software development teams.",
  [SourceType.LINEAR]:
    "Modern issue tracking and project management tool built for high-performance teams.",
  [SourceType.NETSUITE]:
    "Cloud-based ERP platform for financial management, CRM, and e-commerce operations.",
  [SourceType.BROWSERBASE]:
    "Headless browser infrastructure for web scraping and automated data extraction.",
  [SourceType.CLICKHOUSE]:
    "Open-source columnar database optimized for real-time analytics and large-scale data processing.",
  [SourceType.MYSQL]:
    "Widely-used open-source relational database known for speed, reliability, and ease of use.",
  [SourceType.SNOWFLAKE]:
    "Cloud data platform for data warehousing, data lakes, and analytics workloads.",
  [SourceType.GOOGLE_DRIVE]:
    "Google's cloud-based file storage service for personal and team document management and sharing.",
  [SourceType.S3]:
    "Amazon Simple Storage Service for secure, scalable, and durable object storage in the cloud.",
  [SourceType.SALESFORCE]: "Leading CRM platform for sales, service, and marketing automation.",
  [SourceType.ATTIO]:
    "CRM platform designed for modern teams to centralize customer data, pipelines, and workflows.",
  [SourceType.NOTION]:
    "All-in-one productivity app for notes, tasks, databases, and knowledge collaboration.",
  [SourceType.POSTGRES]:
    "Popular open-source relational database management system known for reliability and advanced features.",
  [SourceType.BIGQUERY]:
    "Google's serverless data warehouse for scalable analytics and machine learning workloads.",
  [SourceType.HUBSPOT]:
    "Inbound marketing and sales platform for CRM, email marketing, and customer engagement.",
  [SourceType.GITHUB]:
    "Code hosting platform for version control, collaboration, and software development workflows.",
  [SourceType.CONFLUENCE]:
    "Team workspace for documentation, knowledge management, and project collaboration.",
  [SourceType.WIKIPEDIA]:
    "Wikipedia is a free online encyclopedia with articles written by volunteers from around the world.",
};

export const SOURCE_TYPE_STRING_TO_ENUM_MAP: Record<string, SourceType> = {
  SOURCE_TYPE_UNSPECIFIED: SourceType.UNSPECIFIED,
  SOURCE_TYPE_FILE_UPLOAD: SourceType.FILE_UPLOAD,
  SOURCE_TYPE_WEBHOOK: SourceType.WEBHOOK,
  SOURCE_TYPE_TEXT: SourceType.TEXT,
  SOURCE_TYPE_SLACK: SourceType.SLACK,
  SOURCE_TYPE_JIRA: SourceType.JIRA,
  SOURCE_TYPE_LINEAR: SourceType.LINEAR,
  SOURCE_TYPE_NETSUITE: SourceType.NETSUITE,
  SOURCE_TYPE_BROWSERBASE: SourceType.BROWSERBASE,
  SOURCE_TYPE_CLICKHOUSE: SourceType.CLICKHOUSE,
  SOURCE_TYPE_MYSQL: SourceType.MYSQL,
  SOURCE_TYPE_SNOWFLAKE: SourceType.SNOWFLAKE,
  SOURCE_TYPE_GOOGLE_DRIVE: SourceType.GOOGLE_DRIVE,
  SOURCE_TYPE_S3: SourceType.S3,
  SOURCE_TYPE_SALESFORCE: SourceType.SALESFORCE,
  SOURCE_TYPE_ATTIO: SourceType.ATTIO,
  SOURCE_TYPE_NOTION: SourceType.NOTION,
  SOURCE_TYPE_POSTGRES: SourceType.POSTGRES,
  SOURCE_TYPE_BIGQUERY: SourceType.BIGQUERY,
  SOURCE_TYPE_HUBSPOT: SourceType.HUBSPOT,
  SOURCE_TYPE_GITHUB: SourceType.GITHUB,
  SOURCE_TYPE_CONFLUENCE: SourceType.CONFLUENCE,
  SOURCE_TYPE_WIKIPEDIA: SourceType.WIKIPEDIA,
};

export const SOURCE_STATUS_TO_BEACON_VARIANT_MAP: Record<SourceStatus, BeaconVariant> = {
  [SourceStatus.UNSPECIFIED]: BeaconVariant.DISABLED,
  [SourceStatus.PENDING]: BeaconVariant.BLUE,
  [SourceStatus.READY]: BeaconVariant.SUCCESS,
  [SourceStatus.FAILED]: BeaconVariant.ERROR,
};

export const SOURCE_STATUS_TO_TEXT_MAP: Record<SourceStatus, string> = {
  [SourceStatus.UNSPECIFIED]: "None",
  [SourceStatus.PENDING]: "Pending",
  [SourceStatus.READY]: "Ready",
  [SourceStatus.FAILED]: "Failed",
};

export const SOURCE_STATUS_TO_TEXT_VARIANT_MAP: Record<SourceStatus, TextVariant> = {
  [SourceStatus.UNSPECIFIED]: TextVariant.DISABLED,
  [SourceStatus.PENDING]: TextVariant.BLUE,
  [SourceStatus.READY]: TextVariant.SUCCESS,
  [SourceStatus.FAILED]: TextVariant.ERROR,
};

export const SYNC_STATUS_TO_BEACON_VARIANT_MAP: Record<SourceSyncStatus, BeaconVariant> = {
  [SourceSyncStatus.UNSPECIFIED]: BeaconVariant.DISABLED,
  [SourceSyncStatus.START]: BeaconVariant.BLUE,
  [SourceSyncStatus.START_FAILED]: BeaconVariant.ERROR,
  [SourceSyncStatus.RUNNING]: BeaconVariant.BLUE,
  [SourceSyncStatus.NORMALIZE]: BeaconVariant.BLUE,
  [SourceSyncStatus.NORMALIZE_FAILED]: BeaconVariant.ERROR,
  [SourceSyncStatus.EXTRACT]: BeaconVariant.BLUE,
  [SourceSyncStatus.EXTRACT_FAILED]: BeaconVariant.ERROR,
  [SourceSyncStatus.LOAD]: BeaconVariant.BLUE,
  [SourceSyncStatus.LOAD_FAILED]: BeaconVariant.ERROR,
  [SourceSyncStatus.RESEARCH]: BeaconVariant.BLUE,
  [SourceSyncStatus.RESEARCH_FAILED]: BeaconVariant.ERROR,
  [SourceSyncStatus.COMPLETE]: BeaconVariant.SUCCESS,
  [SourceSyncStatus.FAILED]: BeaconVariant.ERROR,
};

export const SYNC_STATUS_TO_TEXT_MAP: Record<SourceSyncStatus, string> = {
  [SourceSyncStatus.UNSPECIFIED]: "—",
  [SourceSyncStatus.START]: "Starting",
  [SourceSyncStatus.START_FAILED]: "Failed to start",
  [SourceSyncStatus.RUNNING]: "Running",
  [SourceSyncStatus.NORMALIZE]: "Normalizing",
  [SourceSyncStatus.NORMALIZE_FAILED]: "Failed to normalize",
  [SourceSyncStatus.EXTRACT]: "Extracting",
  [SourceSyncStatus.EXTRACT_FAILED]: "Failed to extract",
  [SourceSyncStatus.LOAD]: "Loading",
  [SourceSyncStatus.LOAD_FAILED]: "Failed to load",
  [SourceSyncStatus.RESEARCH]: "Researching",
  [SourceSyncStatus.RESEARCH_FAILED]: "Failed to research",
  [SourceSyncStatus.COMPLETE]: "Complete",
  [SourceSyncStatus.FAILED]: "Failed to complete",
};

export const SYNC_STATUS_TO_TEXT_VARIANT_MAP: Record<SourceSyncStatus, TextVariant> = {
  [SourceSyncStatus.UNSPECIFIED]: TextVariant.DISABLED,
  [SourceSyncStatus.START]: TextVariant.BLUE,
  [SourceSyncStatus.START_FAILED]: TextVariant.ERROR,
  [SourceSyncStatus.RUNNING]: TextVariant.BLUE,
  [SourceSyncStatus.NORMALIZE]: TextVariant.BLUE,
  [SourceSyncStatus.NORMALIZE_FAILED]: TextVariant.ERROR,
  [SourceSyncStatus.EXTRACT]: TextVariant.BLUE,
  [SourceSyncStatus.EXTRACT_FAILED]: TextVariant.ERROR,
  [SourceSyncStatus.LOAD]: TextVariant.BLUE,
  [SourceSyncStatus.LOAD_FAILED]: TextVariant.ERROR,
  [SourceSyncStatus.RESEARCH]: TextVariant.BLUE,
  [SourceSyncStatus.RESEARCH_FAILED]: TextVariant.ERROR,
  [SourceSyncStatus.COMPLETE]: TextVariant.SUCCESS,
  [SourceSyncStatus.FAILED]: TextVariant.ERROR,
};

export const FILTERABLE_SOURCE_STATUSES: SourceStatus[] = [
  SourceStatus.PENDING,
  SourceStatus.READY,
  SourceStatus.FAILED,
];

export const SOURCE_TYPE_TO_FEATURE_FLAG_MAP: Record<SourceType, FeatureFlag | undefined> = {
  [SourceType.UNSPECIFIED]: undefined,
  [SourceType.FILE_UPLOAD]: FeatureFlag.SOURCE_TYPE_FILE_UPLOAD_ENABLED,
  [SourceType.WEBHOOK]: FeatureFlag.SOURCE_TYPE_WEBHOOK_ENABLED,
  [SourceType.TEXT]: FeatureFlag.SOURCE_TYPE_TEXT_ENABLED,
  [SourceType.SLACK]: FeatureFlag.SOURCE_TYPE_SLACK_ENABLED,
  [SourceType.JIRA]: FeatureFlag.SOURCE_TYPE_JIRA_ENABLED,
  [SourceType.LINEAR]: FeatureFlag.SOURCE_TYPE_LINEAR_ENABLED,
  [SourceType.NETSUITE]: FeatureFlag.SOURCE_TYPE_NETSUITE_ENABLED,
  [SourceType.BROWSERBASE]: FeatureFlag.SOURCE_TYPE_BROWSERBASE_ENABLED,
  [SourceType.CLICKHOUSE]: FeatureFlag.SOURCE_TYPE_CLICKHOUSE_ENABLED,
  [SourceType.MYSQL]: FeatureFlag.SOURCE_TYPE_MYSQL_ENABLED,
  [SourceType.SNOWFLAKE]: FeatureFlag.SOURCE_TYPE_SNOWFLAKE_ENABLED,
  [SourceType.GOOGLE_DRIVE]: FeatureFlag.SOURCE_TYPE_GOOGLE_DRIVE_ENABLED,
  [SourceType.S3]: FeatureFlag.SOURCE_TYPE_S3_ENABLED,
  [SourceType.SALESFORCE]: FeatureFlag.SOURCE_TYPE_SALESFORCE_ENABLED,
  [SourceType.ATTIO]: FeatureFlag.SOURCE_TYPE_ATTIO_ENABLED,
  [SourceType.NOTION]: FeatureFlag.SOURCE_TYPE_NOTION_ENABLED,
  [SourceType.POSTGRES]: FeatureFlag.SOURCE_TYPE_POSTGRES_ENABLED,
  [SourceType.BIGQUERY]: FeatureFlag.SOURCE_TYPE_BIGQUERY_ENABLED,
  [SourceType.HUBSPOT]: FeatureFlag.SOURCE_TYPE_HUBSPOT_ENABLED,
  [SourceType.GITHUB]: FeatureFlag.SOURCE_TYPE_GITHUB_ENABLED,
  [SourceType.CONFLUENCE]: FeatureFlag.SOURCE_TYPE_CONFLUENCE_ENABLED,
  [SourceType.WIKIPEDIA]: FeatureFlag.SOURCE_TYPE_WIKIPEDIA_ENABLED,
};

export enum SourceTypeStatusChip {
  NONE = "NONE",
  ENTERPRISE = "ENTERPRISE",
  COMING_SOON = "COMING_SOON",
}

export const SOURCE_TYPE_TO_SOURCE_TYPE_STATUS_CHIP_MAP: Record<SourceType, SourceTypeStatusChip> =
  {
    [SourceType.UNSPECIFIED]: SourceTypeStatusChip.NONE,
    [SourceType.FILE_UPLOAD]: SourceTypeStatusChip.NONE,
    [SourceType.WEBHOOK]: SourceTypeStatusChip.NONE,
    [SourceType.TEXT]: SourceTypeStatusChip.NONE,
    [SourceType.SLACK]: SourceTypeStatusChip.COMING_SOON,
    [SourceType.JIRA]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.LINEAR]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.NETSUITE]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.BROWSERBASE]: SourceTypeStatusChip.NONE,
    [SourceType.CLICKHOUSE]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.MYSQL]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.SNOWFLAKE]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.GOOGLE_DRIVE]: SourceTypeStatusChip.COMING_SOON,
    [SourceType.S3]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.SALESFORCE]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.ATTIO]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.NOTION]: SourceTypeStatusChip.COMING_SOON,
    [SourceType.POSTGRES]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.BIGQUERY]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.HUBSPOT]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.GITHUB]: SourceTypeStatusChip.COMING_SOON,
    [SourceType.CONFLUENCE]: SourceTypeStatusChip.ENTERPRISE,
    [SourceType.WIKIPEDIA]: SourceTypeStatusChip.NONE,
  };
